/* eslint-disable import/no-extraneous-dependencies -- package exports resolve via react-phone-number-input */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import PhoneInput from 'react-phone-number-input/input-max';
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input/max';
import flags from 'react-phone-number-input/flags';
import en from 'react-phone-number-input/locale/en';
import metadata from 'libphonenumber-js/metadata.max.json';
import { Select } from 'design-system';
import { getCompanyCountryCode } from 'helpers/companyCountryHelper';
import 'react-phone-number-input/style.css';
import 'assets/css/PhoneNumberInput.css';

const FLAG_ICONS = flags?.default || flags;

const getCallingCodeLabel = (country) => {
  if (!country) {
    return '';
  }
  try {
    return `+${getCountryCallingCode(country)}`;
  } catch {
    return '';
  }
};

const CountryFlagAvatar = ({ country, label }) => {
  if (!country) {
    return null;
  }
  const Flag = FLAG_ICONS[country];
  const title = label || country;
  if (Flag) {
    return <Flag className="PhoneInputCountryIconImg" title={title} />;
  }
  return (
    <img
      className="PhoneInputCountryIconImg"
      alt={title}
      src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${country}.svg`}
    />
  );
};

CountryFlagAvatar.propTypes = {
  country: PropTypes.string,
  label: PropTypes.string,
};

CountryFlagAvatar.defaultProps = {
  country: undefined,
  label: undefined,
};

const COUNTRY_SELECT_OPTIONS = getCountries().map((code) => {
  const name = en[code] || code;
  const callingCode = getCallingCodeLabel(code);
  return {
    value: code,
    // Full name is searchable (DS Select filters label+subtitle) but visually hidden
    label: `${name} ${code} ${callingCode}`,
    // Visible row: ISO on first line, dial on second (same as sf-react-app)
    subtitle: `${code}\n${callingCode}`,
    avatar: <CountryFlagAvatar country={code} label={name} />,
    callingCode,
  };
});

export const getCountryNationalLengthBounds = (country) => {
  if (!country) {
    return null;
  }

  const countryData = metadata.countries?.[country];
  const lengths = countryData?.[3];
  if (!Array.isArray(lengths) || lengths.length === 0) {
    return null;
  }

  const numeric = lengths.filter((n) => typeof n === 'number');
  if (!numeric.length) {
    return null;
  }

  return {
    lengths: numeric,
    min: Math.min(...numeric),
    max: Math.max(...numeric),
  };
};

export const getMaxNationalDigits = (country) => getCountryNationalLengthBounds(country)?.max ?? null;

const getNationalDigitCount = (value) => {
  if (!value) {
    return 0;
  }
  try {
    return parsePhoneNumber(String(value).trim())?.nationalNumber?.length || 0;
  } catch {
    return String(value).replace(/\D/g, '').length;
  }
};

export const getPhoneCountry = (value, preferredCountry) => {
  if (!value || !String(value).trim()) {
    return undefined;
  }
  try {
    const parsed = parsePhoneNumber(String(value).trim());
    const callingCode = parsed?.countryCallingCode;
    const preferred = preferredCountry || getCompanyCountryCode();

    if (callingCode) {
      const countries = metadata.country_calling_codes?.[String(callingCode)];
      if (Array.isArray(countries) && countries.length > 0) {
        if (preferred && countries.includes(preferred)) {
          return preferred;
        }
        if (parsed?.country) {
          return parsed.country;
        }
        // Shared dial codes (+1, +44, …) are ordered by libphonenumber, not product rules.
        if (String(callingCode) === '44' && countries.includes('GB')) {
          return 'GB';
        }
        if (String(callingCode) === '1' && countries.includes('US')) {
          return 'US';
        }
        return preferred && countries.includes(preferred) ? preferred : countries[0];
      }
    }

    return parsed?.country;
  } catch {
    return undefined;
  }
};

export const getInitialPhoneValue = (value) => {
  if (!value || !String(value).trim()) {
    return '';
  }
  const phone = String(value).trim();
  if (/^\+\d{1,4}$/.test(phone)) {
    return '';
  }
  return phone;
};

export const normalizePhoneValue = (value, countryHint = getCompanyCountryCode()) => {
  const cleaned = getInitialPhoneValue(value);
  if (!cleaned) {
    return '';
  }
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  try {
    const parsed = parsePhoneNumber(cleaned, countryHint || undefined);
    return parsed?.number || cleaned;
  } catch {
    return cleaned;
  }
};

export const isValidEmployeePhone = (value, countryHint = getCompanyCountryCode()) => {
  if (!value || !String(value).trim()) {
    return true;
  }

  const phone = String(value).trim();

  try {
    const parsed = parsePhoneNumber(phone);
    if (parsed?.country) {
      return isValidPhoneNumber(phone);
    }
  } catch {
    // Invalid / unparseable input — fall through to country-hinted validation
  }

  if (countryHint) {
    return isValidPhoneNumber(phone, countryHint);
  }

  return isValidPhoneNumber(phone);
};

export const getPhoneValidationMessage = (intl, country, value) => {
  const bounds = getCountryNationalLengthBounds(country);
  const current = getNationalDigitCount(value);

  if (!bounds) {
    return intl.formatMessage({
      id: 'invalid-phone-number-format',
      defaultMessage: 'Invalid phone number for the selected country.',
    });
  }

  if (current < bounds.min) {
    if (bounds.min === bounds.max) {
      return intl.formatMessage(
        {
          id: 'phone-number-must-be-digits-for-country',
          defaultMessage: 'Phone number must be {count} digits for the selected country ({current}/{count}).',
        },
        { count: bounds.max, current }
      );
    }

    return intl.formatMessage(
      {
        id: 'phone-number-must-be-digits-range-for-country',
        defaultMessage: 'Phone number must be {min}–{max} digits for the selected country ({current}/{max}).',
      },
      { min: bounds.min, max: bounds.max, current }
    );
  }

  return intl.formatMessage({
    id: 'invalid-phone-number-format',
    defaultMessage: 'Invalid phone number for the selected country.',
  });
};

export const getPhoneYupTest = (intl) => ({
  name: 'valid-phone',
  test(value) {
    const countryHint =
      getPhoneCountry(value, this.parent?.phoneCountry) || this.parent?.phoneCountry || getCompanyCountryCode();
    if (isValidEmployeePhone(value, countryHint)) {
      return true;
    }
    return this.createError({
      message: getPhoneValidationMessage(intl, countryHint, value),
    });
  },
});

export const getPhoneLengthValidationMessage = getPhoneValidationMessage;

const COUNTRY_MENU_VIEWPORT_GAP = 8;
const COUNTRY_LIST_MAX_PX = 200;
const COUNTRY_LIST_MIN_PX = 200;
const COUNTRY_SEARCH_CHROME_PX = 72;
const NO_MATCH_CLASS = 'sf-phone-country-empty';
const MENU_UP_CLASS = 'sf-phone-country-select-wrap--menu-up';

const getVisibleMenuBounds = (anchorEl) => {
  let top = 0;
  let bottom = window.innerHeight;

  let node = anchorEl.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY || style.overflow;
    if (/(hidden|auto|scroll|clip)/.test(overflowY)) {
      const r = node.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
    }
    node = node.parentElement;
  }

  const footer = document.querySelector(
    '[class*="BgBottomProgressBar"], [class*="BottomProgress"]'
  );
  if (footer) {
    const footerRect = footer.getBoundingClientRect();
    if (footerRect.height > 0) {
      bottom = Math.min(bottom, footerRect.top);
    }
  }

  return { top, bottom };
};

const CountrySelect = ({ value, onChange, disabled }) => {
  const intl = useIntl();
  const noMatchLabel = intl.formatMessage({ id: 'no-match-found' });
  const wrapRef = useRef(null);
  const [openUpward, setOpenUpward] = useState(false);
  const options = useMemo(() => COUNTRY_SELECT_OPTIONS, []);
  const selectedValue =
    value && options.some((opt) => opt.value === value) ? value : options[0]?.value || 'GB';

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) {
      return undefined;
    }

    let rafId = 0;

    const syncEmptyState = (menuEl) => {
      const list = menuEl?.querySelector('.ds-field-select__list');
      if (!list) {
        return 0;
      }
      const optionCount = list.querySelectorAll('.ds-field-select__option').length;
      let emptyEl = list.querySelector(`.${NO_MATCH_CLASS}`);
      if (optionCount === 0) {
        if (!emptyEl) {
          emptyEl = document.createElement('div');
          emptyEl.className = NO_MATCH_CLASS;
          emptyEl.setAttribute('role', 'status');
          list.appendChild(emptyEl);
        }
        emptyEl.textContent = noMatchLabel;
      } else if (emptyEl) {
        emptyEl.remove();
      }
      return optionCount;
    };

    const updatePlacement = () => {
      const selectRoot = wrap.querySelector('.ds-field-select');
      const isOpen = Boolean(selectRoot?.classList.contains('ds-field-select--open'));
      if (!isOpen) {
        wrap.classList.remove(MENU_UP_CLASS);
        setOpenUpward(false);
        return;
      }

      const menuEl = wrap.querySelector('.sf-phone-country-select-menu');
      const optionCount = syncEmptyState(menuEl);
      const trigger =
        wrap.querySelector('.ds-field-select__trigger-wrap') || selectRoot || wrap;
      const rect = trigger.getBoundingClientRect();
      const bounds = getVisibleMenuBounds(wrap);
      const spaceBelow = bounds.bottom - rect.bottom - COUNTRY_MENU_VIEWPORT_GAP;
      const spaceAbove = rect.top - bounds.top - COUNTRY_MENU_VIEWPORT_GAP;

      const searchEl = menuEl?.querySelector('.ds-field-select__search');
      const chrome = (searchEl?.offsetHeight || 56) + 16;
      const rowsHeight =
        optionCount === 0
          ? COUNTRY_LIST_MIN_PX
          : Math.min(COUNTRY_LIST_MAX_PX, Math.max(COUNTRY_LIST_MIN_PX, optionCount * 48));
      const needed = Math.min(
        chrome + COUNTRY_LIST_MAX_PX,
        Math.max(COUNTRY_SEARCH_CHROME_PX + COUNTRY_LIST_MIN_PX, chrome + rowsHeight)
      );

      // Prefer opening above whenever the field is low on the page or below-space can't fit the menu
      const inLowerHalf = rect.bottom > (bounds.top + bounds.bottom) / 2;
      const shouldOpenUp =
        (spaceBelow < needed && spaceAbove > 0) ||
        (inLowerHalf && spaceAbove >= spaceBelow);

      wrap.classList.toggle(MENU_UP_CLASS, shouldOpenUp);
      setOpenUpward(shouldOpenUp);

      const available = Math.max(
        COUNTRY_LIST_MIN_PX,
        (shouldOpenUp ? spaceAbove : Math.max(spaceBelow, 0)) - chrome
      );
      if (selectRoot) {
        selectRoot.style.setProperty(
          '--ds-field-select-list-max-height',
          `${Math.min(COUNTRY_LIST_MAX_PX, available)}px`
        );
      }
    };

    const schedulePlacement = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(updatePlacement);
      });
    };

    const observer = new MutationObserver(schedulePlacement);
    observer.observe(wrap, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class'],
      childList: true,
    });

    wrap.addEventListener('input', schedulePlacement, true);
    window.addEventListener('resize', schedulePlacement);
    window.addEventListener('scroll', schedulePlacement, true);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      wrap.removeEventListener('input', schedulePlacement, true);
      window.removeEventListener('resize', schedulePlacement);
      window.removeEventListener('scroll', schedulePlacement, true);
    };
  }, [noMatchLabel]);

  return (
    <div
      ref={wrapRef}
      className={[
        'sf-phone-country-select-wrap',
        openUpward ? 'sf-phone-country-select-wrap--menu-up' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Select
        variant="affix"
        className="sf-phone-country-select"
        searchable
        searchPlaceholder="Search..."
        options={options}
        value={selectedValue}
        onChange={(next) => {
          if (next) {
            onChange(next);
          }
        }}
        disabled={disabled}
        ariaLabel="Country code"
        menuMinWidth="12.5rem"
        menuMaxWidth="20rem"
        listMaxHeight="220px"
        menuClassName="sf-phone-country-select-menu"
        renderSelectedLabel={(opt) =>
          opt.callingCode || getCallingCodeLabel(opt.value) || opt.value
        }
      />
    </div>
  );
};

CountrySelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

CountrySelect.defaultProps = {
  value: undefined,
  disabled: false,
};

const wouldExceedMaxNationalLength = (nextValue, countryHint) => {
  if (!nextValue) {
    return false;
  }

  try {
    const parsed = parsePhoneNumber(nextValue);
    const country = parsed?.country || countryHint;
    const maxDigits = getMaxNationalDigits(country);
    if (!maxDigits || !parsed?.nationalNumber) {
      return false;
    }
    return parsed.nationalNumber.length > maxDigits;
  } catch {
    return false;
  }
};

const PhoneNumberInput = ({ value, onChange, onBlur, name, id, placeholder, disabled, defaultCountry, className }) => {
  const companyCountry = getCompanyCountryCode();
  const [pickedCountry, setPickedCountry] = useState(defaultCountry || null);

  useEffect(() => {
    if (defaultCountry) {
      setPickedCountry(defaultCountry);
    }
  }, [defaultCountry]);

  const activeCountry =
    pickedCountry ||
    defaultCountry ||
    getPhoneCountry(value, companyCountry) ||
    companyCountry;

  const maxNationalDigits = getMaxNationalDigits(activeCountry);
  const nationalDigitCount = getNationalDigitCount(value);
  const atMaxLength = maxNationalDigits != null && nationalDigitCount >= maxNationalDigits;

  const handleCountryChange = (country) => {
    const nextCountry = country || companyCountry;
    setPickedCountry(nextCountry);
    onChange('', nextCountry);
  };

  return (
    <div className={`sf-phone-input PhoneInput ${className || ''}`.trim()}>
      <CountrySelect value={activeCountry} onChange={handleCountryChange} disabled={disabled} />
      <PhoneInput
        key={activeCountry}
        country={activeCountry}
        international={false}
        useNationalFormatForDefaultCountryValue
        value={value || undefined}
        onChange={(next) => {
          const nextValue = next || '';

          if (wouldExceedMaxNationalLength(nextValue, activeCountry)) {
            return;
          }

          const nextCountry = getPhoneCountry(nextValue, activeCountry) || activeCountry;
          setPickedCountry(nextCountry);
          onChange(nextValue, nextCountry);
        }}
        onBlur={onBlur}
        name={name}
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        className="form-control PhoneInputInput"
        onKeyPress={(event) => {
          if (!/[0-9]/.test(event.key)) {
            event.preventDefault();
            return;
          }
          const input = event.target;
          const hasSelection =
            typeof input?.selectionStart === 'number' &&
            typeof input?.selectionEnd === 'number' &&
            input.selectionStart !== input.selectionEnd;
          if (atMaxLength && !hasSelection) {
            event.preventDefault();
          }
        }}
      />
    </div>
  );
};

PhoneNumberInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  defaultCountry: PropTypes.string,
  className: PropTypes.string,
};

PhoneNumberInput.defaultProps = {
  value: '',
  onBlur: undefined,
  name: 'phone',
  id: undefined,
  placeholder: undefined,
  disabled: false,
  defaultCountry: undefined,
  className: '',
};

export const getPhoneDialCode = (countryOrValue, preferredCountry) => {
  try {
    if (countryOrValue && String(countryOrValue).startsWith('+')) {
      const parsed = parsePhoneNumber(String(countryOrValue).trim());
      return parsed?.countryCallingCode || undefined;
    }
    if (countryOrValue && /^[A-Z]{2}$/i.test(String(countryOrValue))) {
      return getCountryCallingCode(String(countryOrValue).toUpperCase());
    }
    if (preferredCountry) {
      return getCountryCallingCode(preferredCountry);
    }
  } catch {
    return undefined;
  }
  return undefined;
};

export const formatPhoneAnswerForPayload = (answer = {}) => {
  const raw = String(answer.value || '').trim();
  if (!raw) {
    return null;
  }

  let value = raw;
  let country = answer.phoneCountry || answer.country || getPhoneCountry(raw) || undefined;
  let code = answer.code;

  try {
    if (!value.startsWith('+') && code) {
      value = `+${String(code).replace(/^\+/, '')}${value}`;
    }
    const parsed = parsePhoneNumber(value, country || undefined);
    if (parsed?.number) {
      value = parsed.number;
      country = parsed.country || country;
      code = parsed.countryCallingCode || code;
    }
  } catch {
    // Keep the raw value if it cannot be parsed
  }

  if (!code && country) {
    code = getPhoneDialCode(country);
  }

  const payload = {
    ...answer,
    value,
    type: answer.type || 'text',
  };

  if (code) {
    payload.code = String(code).replace(/^\+/, '');
  }
  if (country) {
    payload.country = country;
  }
  delete payload.phoneCountry;

  return payload;
};

export { isValidPhoneNumber };
export default PhoneNumberInput;
